import { Scene } from "@babylonjs/core/scene";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { SelectionOutlineLayer } from "@babylonjs/core/Layers/selectionOutlineLayer";
import "@babylonjs/core/Layers/effectLayerSceneComponent";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { TraceScene } from "../core/TraceScene";
import type { SceneObject } from "../core/SceneObject";
import { COLLIDER_MESH_SUFFIX, SELECTION_MESH_SUFFIX } from "../behaviors/boxcollider";
import { Logger } from "../utils/Logger";

/**
 * Manages the visual selection outline.
 * Listens to `trace-viewer:selection-changed` and `trace-viewer:playmode-changed`
 * events so the outline stays in sync regardless of how selection is triggered.
 */
export class SelectionOutline {
    private readonly scene: Scene;
    private readonly traceScene: TraceScene;
    private outlineLayer: SelectionOutlineLayer | null = null;

    private _selectionHandler: ((e: Event) => void) | null = null;
    private _playModeHandler: ((e: Event) => void) | null = null;

    // When an editor drives a multi-object selection, it sets this set and the
    // single-object selection-changed event no longer overrides the outline.
    // Null means "follow the runtime's single primary selection" (default).
    private _outlinedSet: Set<SceneObject> | null = null;

    constructor(scene: Scene, traceScene: TraceScene) {
        this.scene = scene;
        this.traceScene = traceScene;
    }

    /** Create the outline layer and start listening for events. */
    public enable(): void {
        if (!this.outlineLayer) {
            this.outlineLayer = new SelectionOutlineLayer("selectionOutline", this.scene);
            this.outlineLayer.outlineColor = new Color3(1, 0.5, 0); // Orange
            this.outlineLayer.outlineThickness = 2;
            Logger.log("[SelectionOutline] Created outline layer");
        }

        this._listenForEvents();
    }

    /** Subscribe to selection-changed and playmode-changed on the viewer element. */
    private _listenForEvents(): void {
        const evt = this.traceScene?.getEventSystem();
        if (!evt) return;

        if (!this._selectionHandler) {
            this._selectionHandler = (e: Event) => {
                // An editor-driven multi-selection set takes precedence over the
                // runtime's single primary; ignore the single event while it's active.
                //
                // Ownership contract: while a set is active, single `select()`
                // calls (undo/redo restoring inspector focus, programmatic selects)
                // do NOT update the outline. The editor OWNS the outline here and
                // must call `setOutlinedSet(null)` (or a fresh set) when the set is
                // no longer authoritative — e.g. when a batch op or undo collapses
                // the selection back to one object. The sandbox does this by
                // releasing to null whenever its selection set drops to ≤1.
                if (this._outlinedSet !== null) return;
                const detail = (e as CustomEvent<{ object?: SceneObject }>).detail;
                const sceneObject: SceneObject | null = detail?.object ?? null;
                this._sync(sceneObject);
            };
            evt.addEventListener('trace-viewer:selection-changed', this._selectionHandler);
        }

        if (!this._playModeHandler) {
            this._playModeHandler = (e: Event) => {
                const playMode: boolean = (e as CustomEvent<{ playMode: boolean }>).detail?.playMode;

                if (playMode) {
                    // Entering play mode — clear outline (selection is cleared by TraceScene)
                    this._clear();
                } else {
                    // Exiting play mode — the runtime tracks a single primary, so any
                    // editor multi-set is stale. Release it (else the guard in the
                    // selection-changed handler would freeze the outline) and restore
                    // the outline for the current single selection.
                    this._outlinedSet = null;
                    const selected = this.traceScene?.getSelectedObject() ?? null;
                    this._sync(selected);
                }
            };
            evt.addEventListener('trace-viewer:playmode-changed', this._playModeHandler);
        }

        Logger.log("[SelectionOutline] Listening for selection/playmode events");
    }

    /** Remove event listeners. */
    private _removeListeners(): void {
        const evt = this.traceScene?.getEventSystem();
        if (evt) {
            if (this._selectionHandler) {
                evt.removeEventListener('trace-viewer:selection-changed', this._selectionHandler);
            }
            if (this._playModeHandler) {
                evt.removeEventListener('trace-viewer:playmode-changed', this._playModeHandler);
            }
        }
        this._selectionHandler = null;
        this._playModeHandler = null;
    }

    /**
     * Outline an explicit set of objects (editor multi-selection).
     *
     * Pass a non-empty set to take over outlining from the single-object
     * selection-changed path. Pass `null` — or an empty set — to release control
     * and fall back to following the runtime's single primary selection. (An
     * empty set is treated as `null`: latching a non-null empty set would leave
     * the outline blind to every subsequent selection-changed event.)
     */
    public setOutlinedSet(objects: ReadonlySet<SceneObject> | null): void {
        if (objects === null || objects.size === 0) {
            this._outlinedSet = null;
            // Fall back to whatever the runtime currently has selected.
            this._sync(this.traceScene?.getSelectedObject() ?? null);
            return;
        }

        this._outlinedSet = new Set(objects);
        this._clear();
        for (const obj of this._outlinedSet) {
            this._outline(obj.transform);
        }
        Logger.log(`[SelectionOutline] Outlined set of ${this._outlinedSet.size}`);
    }

    /** Sync the outline to match the given single selection. */
    private _sync(sceneObject: SceneObject | null): void {
        this._clear();

        if (sceneObject) {
            this._outline(sceneObject.transform);
            Logger.log(`[SelectionOutline] Outlined: ${sceneObject.name}`);
        }
    }

    private _clear(): void {
        this.outlineLayer?.clearSelection();
    }

    /** Outline an object and its children as a group. */
    private _outline(transform: TransformNode): void {
        if (!this.outlineLayer) return;
        // A caller-supplied set (editor multi-selection) may hold a node that was
        // disposed out from under it (deleted via a path that didn't refresh the
        // set). Skip disposed nodes rather than walking their stale child tree.
        if (transform.isDisposed()) return;

        const meshes: AbstractMesh[] = [];

        if (transform instanceof AbstractMesh) {
            meshes.push(transform);
        }

        meshes.push(...transform.getChildMeshes(false).filter(
            m => !m.name.endsWith(COLLIDER_MESH_SUFFIX) && !m.name.endsWith(SELECTION_MESH_SUFFIX)
        ));

        if (meshes.length > 0) {
            this.outlineLayer.addSelection(meshes);
        }
    }

    /**
     * Reset the outline layer after clearScene().
     * clearScene() disposes effect layers, leaving the outline layer in a broken state.
     * Disposes the old layer and immediately recreates it with fresh listeners.
     */
    public resetOutlineLayer(): void {
        if (!this.outlineLayer) return;
        // Release any editor multi-set: it references SceneObjects from the scene
        // being torn down, and leaving it non-null would keep the guard swallowing
        // every selection-changed event for the newly loaded scene. Same fix as
        // the play-mode-exit path, generalized to this second reset entry point.
        this._outlinedSet = null;
        this._removeListeners();
        this.outlineLayer.dispose();
        this.outlineLayer = null;
        this.enable();
    }
}
